import React, { useEffect } from 'react';
import RocketIcon from 'public/icons/rocket.svg';
import { Box, Chip, Divider } from '@mui/material';

import { EPositionType, IEnrichedUniswapPosition } from '../../../../types/uniswapTokens';
import { ListItemExpandedWrapper } from '../../../../modules/dashboard/lists/ListItemExpandedWrapper';
import { ListValueColumn } from '../../../../modules/dashboard/lists/ListValueColumn';
import { ListUniswapValueColumn } from '../../../../modules/dashboard/lists/ListUniswapValueColumn';
import { PositionDetails } from '../../../../modules/leverage/PositionDetails';
import { POOL_TYPE_FILTERS } from '../../../../modules/leverage/lists/ToLeveragePositionsList/constants';
import { useConfiguredAutomationsQuery } from '../../../../hooks/yldr/useConfiguredAutomationsQuery';
import { DetailsNumberLine } from '../../../../modals/transactions/FlowCommons/TxModalDetails';
import { ListColumn } from '../../../lists/ListColumn';
import { useEstAprValues } from '../../hooks/useEstAprValues';
import { IEstNetAprState } from '../../types';

export interface IUniswapPositionsListItem {
  position: IEnrichedUniswapPosition;
  actions: (args: {
    position: IEnrichedUniswapPosition;
    tokenId?: string;
    positionAddress?: string;
    isEmptyPosition?: boolean;
    uniswapLink?: string;
  }) => React.ReactNode;
  columnsType: 'lending' | 'leverage' | 'leveraged';
  setEstNetAprHandler?: (params: IEstNetAprState) => void;
}

export const PositionsListItem = ({
  position,
  columnsType,
  setEstNetAprHandler,
  ...rest
}: IUniswapPositionsListItem) => {
  const poolTypeLabel = POOL_TYPE_FILTERS.find((item) => item.value === position.poolType)?.label;
  const { data: configuredAutomationsData } = useConfiguredAutomationsQuery({
    marketId: position.marketId,
    positions: position.positionAddress ? [position.positionAddress] : undefined,
  });

  const autoCompoundInitialized = configuredAutomationsData?.compounds[0].initialized;
  const autoCompoundValue = configuredAutomationsData?.compounds[0].maxTotalFeePercent.div(100).toNumber();

  const {
    estApr,
    estNetApr,
    borrowApy,
    revenueFee,
    netWorthUsd,
    isLoading,
  } = useEstAprValues({ position, autoCompoundInitialized, autoCompoundValue });

  useEffect(() => {
    setEstNetAprHandler?.({
      tokenId: position.tokenId,
      estNetApr,
      isLoading,
    });
  }, [position.tokenId, estNetApr, isLoading]);

  const isLoadingEstNetApr = position.type !== EPositionType.Pool && isLoading;

  return (
    <ListItemExpandedWrapper
      {...position}
      details={
        <PositionDetails
          {...position}
          columnsType={columnsType}
          notLeveraged={true}
        />
      }
    >
      {columnsType === 'leverage' && (
        <ListColumn>
          {poolTypeLabel && <Chip label={poolTypeLabel} />}
        </ListColumn>
      )}

      <ListUniswapValueColumn
        value={position.marketReferenceCurrencyValue}
        disabled={!position.marketReferenceCurrencyValue}
      />

      {columnsType === 'lending' && (
          <ListUniswapValueColumn
            value={
              position.token0.marketReferenceCurrencyFee + position.token1.marketReferenceCurrencyFee
            }
          />
      )}

      {columnsType === 'leverage' && (
        <>
          <ListValueColumn
            value={estNetApr}
            percent={true}
            disabled={estNetApr <= 0}
            loading={isLoadingEstNetApr}
            color='text.brand'
            capsComponent={
              estNetApr > 0
                ? <Box sx={{ ml: 1, mb: '-3px' }}><RocketIcon /></Box>
                : undefined
            }
          />
          <ListValueColumn
            value={position.maxLeverage}
          />
        </>
      )}

      {columnsType === 'leveraged' && (
        <>
          <ListUniswapValueColumn
            value={position.marketReferenceCurrencyDebt || 0}
          />

          <ListUniswapValueColumn
            value={
              position.token0.marketReferenceCurrencyFee + position.token1.marketReferenceCurrencyFee
            }
          />

          <ListUniswapValueColumn value={netWorthUsd} />

          <ListValueColumn
            value={estNetApr}
            color={
              estNetApr >= 0
                ? estNetApr > 0 ? 'text.brand' : undefined
                : 'error.dark'
            }
            underline={true}
            loading={isLoadingEstNetApr}
            percent
            tooltip={
              <>
                <DetailsNumberLine
                  description="Estimated APR"
                  value={estApr}
                  percent={true}
                />
                <DetailsNumberLine
                  description="Total Borrow APY"
                  value={borrowApy ? `-${borrowApy}` : 0}
                  percent={true}
                />
                <DetailsNumberLine
                  description="Protocol Fee"
                  value={revenueFee ? `-${revenueFee}` : 0}
                  percent={true}
                />
                <Divider sx={{ mb: 1.5 }} />
                <DetailsNumberLine
                  description="Estimated Net APR"
                  value={estNetApr}
                  percent={true}
                />
              </>
            }
          />

          <ListValueColumn
            value={position.healthFactor || 0}
          />
        </>
      )}

      {rest.actions({
        position,
        tokenId: position.tokenId.toString(),
        positionAddress: position.positionAddress,
        isEmptyPosition: position.type === EPositionType.Pool,
        uniswapLink: position.uniswapLink,
      })}
    </ListItemExpandedWrapper>
  );
};
